import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { BarChart3, Building2, RefreshCw, TrendingUp } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  TailAdminEmptyState,
  TailAdminPageHeader,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAnalytics();
      setData(res.data || {});
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Platform analytics"
        title="Growth Analytics"
        description="Track enrollment growth and identify top-performing clients across the platform."
        actions={
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <TailAdminPanel
        title="Monthly Enrollment Growth"
        description="Last 6 months of platform enrollment volume."
        action={<TrendingUp className="h-5 w-5 text-brand-500" />}
      >
        {loading ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data?.monthly_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" />
              <XAxis dataKey="month" stroke="#98a2b3" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#98a2b3" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="enrollments" stroke="#465fff" strokeWidth={3} dot={{ fill: "#465fff", r: 4 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </TailAdminPanel>

      <TailAdminPanel
        title="Top Clients by Enrollments"
        description="Active enrollment performance compared with total activity."
        action={<Building2 className="h-5 w-5 text-brand-500" />}
      >
        {loading ? (
          <Skeleton className="h-72 w-full" />
        ) : data?.top_clients?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.top_clients} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" horizontal={false} />
              <XAxis type="number" stroke="#98a2b3" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={120} stroke="#98a2b3" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip />
              <Bar dataKey="enrollments" name="Total" fill="#465fff" radius={[0, 8, 8, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <TailAdminEmptyState icon={BarChart3} title="No client data available" description="Analytics will appear when clients begin generating enrollments." />
        )}
      </TailAdminPanel>
    </div>
  );
};

export default AdminAnalytics;
